/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, {Component, createRef} from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import HighlightedTextComponent from '@/ui/highlighted-text';
import styles from './styles.module.scss';

const propTypes = {
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  values: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      text: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  variant: PropTypes.string,
  name: PropTypes.string,
  className: PropTypes.string,
  tabIndex: PropTypes.number,
  label: PropTypes.node,
  withFiltering: PropTypes.bool,
  placeholder: PropTypes.string
};
const defaultProps = {
  placeholder: '',
  label: null,
  variant: null
};

class Dropdown extends Component {
  /**
   * @param {string} value
   * @param {object} props
   *
   * @returns {integer}
   */
  static findIndexByValue(value, props) {
    const {values = []} = props;
    let indexTemp = -1;

    values.forEach((item, index) => {
      if (item.value === value) {
        indexTemp = index;
      }
    });

    return indexTemp;
  }

  static isKeyboardNavigationEvent(event) {
    return event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter';
  }

  static isNotOverflowing(element) {
    const {overflow} = element.style;

    if (!overflow || overflow === 'visible') element.style.overflow = 'hidden';

    const {clientWidth, scrollWidth, clientHeight, scrollHeight} = element;
    const notOverflowing = clientWidth < scrollWidth || clientHeight < scrollHeight;

    element.style.overflow = overflow;

    return notOverflowing;
  }

  /**
   * @param {object} props
   */
  constructor(props) {
    super();

    const {defaultValue = null} = props;
    let index = -1;

    if (defaultValue !== null) {
      index = Dropdown.findIndexByValue(defaultValue, props);
    }

    this.state = {
      open: false,
      selectedIndex: index,
      up: false,
      clickAnimation: false,
      toggled: false,
      highlightedIndex: -1,
      filteringParameter: null
    };

    this.originalIndexMap = props.values.reduce((map, currentItem, currentIndex) => {
      map[currentItem.value] = currentIndex;
      return map;
    }, {});

    this.listRef = createRef();
    this.dropdownRef = createRef();
    this.dropdownListContainerRef = createRef();
    this.searchInputRef = createRef();
  }

  /**
   * @returns {void}
   */
  placeholderClick = () => {
    const {open} = this.state;
    if (open) {
      this.closeDropdown();
      this.setState({toggled: true}, () => setTimeout(() => this.setState({toggled: false}), 100));
    }
  };

  /**
   * @returns {void}
   */
  placeholderFocus = event => {
    const {toggled, open} = this.state;
    if (toggled) {
      this.closeDropdown();
      event.preventDefault();
      return;
    }

    if (!open) {
      this.openDropdown();
    }
  };

  /**
   * @param {*} event
   */
  handleKeyDown = event => {
    const {open} = this.state;
    if (event.key === 'Tab') {
      if (open) {
        this.closeDropdown();
      }
      return;
    }

    if (!Dropdown.isKeyboardNavigationEvent(event)) {
      this.searchInputRef?.current?.focus();
      return;
    }
    // prevent default only for keyboard navigation
    event.preventDefault();

    const {highlightedIndex} = this.state;
    const {values} = this.props;
    let highlightedNew = highlightedIndex;

    if (event.key === 'ArrowDown') {
      highlightedNew = highlightedIndex + 1;
    }
    if (event.key === 'ArrowUp') {
      highlightedNew = highlightedIndex - 1;
    }
    if (event.key === 'Enter' && highlightedIndex !== -1) {
      const highlightItem = this.getFilteredValues(values)[highlightedIndex];
      const originalIndex = this.originalIndexMap[highlightItem.value];

      this.setSelectedItem(originalIndex);
      this.closeDropdown();
    }

    if (highlightedNew >= this.getFilteredValues(values).length) {
      highlightedNew = 0;
    }
    if (highlightedNew < 0) {
      highlightedNew = this.getFilteredValues(values).length - 1;
    }

    // scroll to new highlighted index
    if (highlightedNew !== highlightedIndex && this.listRef.current) {
      const selector = `li[data-position="${highlightedNew}"]`;
      const scrollContainer = this.dropdownRef.current;
      const elem = this.listRef.current.querySelector(selector);

      if (!Dropdown.isNotOverflowing(scrollContainer)) {
        elem.scrollIntoView({behavior: 'smooth', block: 'center', inline: 'nearest'});
      }
    }

    this.setState({
      highlightedIndex: highlightedNew
    });
  };

  /**
   * @returns {void}
   */
  placeholderBlur = event => {
    this.closeDropdown();
  };

  /**
   * @returns {void}
   */
  openDropdown = () => {
    let renderUp = false;
    const dropdownElement = this.dropdownRef.current;
    const listContainerElement = this.dropdownListContainerRef.current;
    const listElement = this.listRef.current;
    const {withFiltering} = this.props;

    // when filtering is enabled reset the search input when dropdown opens
    if (withFiltering) this.setState({filteringParameter: null});

    if (dropdownElement && listElement) {
      const dropdownOffsetBottom = window.innerHeight - dropdownElement.getBoundingClientRect().top;
      const dropdownHeight = dropdownElement.clientHeight;
      const listContainerHeight = listContainerElement.clientHeight;
      const dropdownListPadding = 40;
      const totalDropdownHeight = listContainerHeight + dropdownHeight + dropdownListPadding;

      renderUp = dropdownOffsetBottom <= totalDropdownHeight;
    }

    this.setState({
      open: true,
      up: renderUp
    });
  };

  /**
   * @returns {void}
   */
  closeDropdown = () => {
    this.setState({
      open: false,
      highlightedIndex: -1
    });
  };

  /**
   * @param {integer} index
   *
   * @returns {void}
   */
  setSelectedItem = index => {
    this.setState(
      {
        selectedIndex: index
      },
      this.emitChange
    );
  };

  /**
   * @returns {void}
   */
  emitChange = () => {
    const {selectedIndex} = this.state;
    const {onChange, values} = this.props;

    // emit on change value
    if (onChange !== null && typeof onChange === 'function') {
      onChange(values[selectedIndex]);
    }
  };

  clickItem = index => {
    this.setSelectedItem(index);
    this.closeDropdown();
  };

  /**
   * @returns {string}
   */
  getDropdownPlaceholder = () => {
    const {selectedIndex} = this.state;
    const {values, placeholder} = this.props;

    if (selectedIndex !== -1 && values[selectedIndex].value !== -1) {
      return values[selectedIndex].text;
    }

    return placeholder;
  };

  /**
   * @returns {string}
   */
  getDropdownValue = () => {
    const {selectedIndex} = this.state;
    const {values} = this.props;

    if (selectedIndex !== -1) {
      return values[selectedIndex].value;
    }

    return null;
  };

  /**
   * @returns {void}
   * */
  clearFilteringValue = () => this.setState({filteringParameter: null});

  /**
   *
   * @param {object} event
   * @returns {void}
   * */
  filterDropdownValues = event => {
    const filteringValue = event?.target?.value;

    if (!filteringValue?.trim()) return this.clearFilteringValue();

    this.setState({filteringParameter: filteringValue});
  };

  /**
   * Check whether or not the given index is the currently selected one
   * @param {string} value
   *
   * @returns {boolean}
   */
  isSelected = index => {
    const {selectedIndex} = this.state;
    return selectedIndex === index;
  };

  getFilteredValues = values => {
    const {filteringParameter} = this.state;
    return values.filter(
      item =>
        !filteringParameter || item.text?.toLowerCase().includes(filteringParameter.toLowerCase())
    );
  };

  /**
   * Select item by value
   * Called externally via ref (e.g., from pricing/plans-section/plan.js)
   * @param {string|number} value
   * @returns {void}
   */
  // eslint-disable-next-line react/no-unused-class-component-methods
  selectItemByValue = value => {
    const index = Dropdown.findIndexByValue(value, this.props);
    if (index !== -1) {
      this.setSelectedItem(index);
    }
  };

  /**
   * Trigger click animation
   * Called externally via ref (e.g., from pricing/plans-section/plan.js)
   * @returns {void}
   */
  // eslint-disable-next-line react/no-unused-class-component-methods
  clickAnimation = () => {
    this.setState({clickAnimation: true}, () => {
      setTimeout(() => this.setState({clickAnimation: false}), 100);
    });
  };

  render() {
    const {
      variant,
      values,
      name = '',
      className = '',
      tabIndex = 0,
      label = '',
      withFiltering = false
    } = this.props;
    const {open, selectedIndex, up, clickAnimation, highlightedIndex, filteringParameter} =
      this.state;
    const dropdownValues = this.getFilteredValues(values);

    const comboboxId = `${name}-combobox`;
    return (
      <div
        ref={this.dropdownRef}
        id={comboboxId}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={`${name}-listbox`}
        aria-label={label || 'Dropdown'}
        aria-describedby={`${name}-description`}
        tabIndex={tabIndex}
        onFocus={(!open && this.placeholderFocus) || undefined}
        onBlur={this.placeholderBlur}
        onClick={(!open && this.placeholderFocus) || undefined}
        onKeyDown={this.handleKeyDown}
        className={`${cx(styles.dropdown, {
          [styles['dropdown-up']]: up,
          [styles[`dropdown-${variant}`]]: variant !== null,
          [styles['dropdown-click-animation']]: clickAnimation,
          [styles.open]: open,
          [styles.filled]: selectedIndex !== -1 && dropdownValues[selectedIndex]?.value !== -1
        })} ${className}`}>
        {/* Hidden input for forms and analytics */}
        <input name={name} value={this.getDropdownValue()} type="hidden" />

        <div
          className={styles['dropdown-input']}
          role="button"
          onMouseDown={this.placeholderClick}
          onKeyDown={() => {}}>
          {label && (
            <label htmlFor={comboboxId} className={styles['dropdown-label']}>
              {label}
            </label>
          )}
          <span className={styles['dropdown-input-text']}>{this.getDropdownPlaceholder()}</span>
        </div>

        <div ref={this.dropdownListContainerRef} className={styles['dropdown-list']}>
          {withFiltering && (
            <div className={styles['dropdown-filter-wrapper']} onClick={e => e.preventDefault()}>
              <img
                src="/static/images/ui/dropdown-search.svg"
                className={styles['dropdown-filter-wrapper__search-icon']}
                alt="Search icon"
              />
              <input
                ref={this.searchInputRef}
                onBlur={this.placeholderBlur}
                name={`filter-${name}`}
                value={filteringParameter || ''}
                placeholder={`Search for ${name}`}
                onChange={this.filterDropdownValues}
                className={styles['dropdown-filter-wrapper__input']}
              />

              {filteringParameter?.length && (
                <img
                  src="/static/images/ui/clear-search.svg"
                  className={styles['dropdown-filter-wrapper__clear-icon']}
                  onClick={this.clearFilteringValue}
                  alt="Clear search icon"
                />
              )}
            </div>
          )}

          <ol ref={this.listRef}>
            {(dropdownValues.length &&
              dropdownValues.map((item, index) => (
                <li
                  className={cx({
                    [styles.active]: this.isSelected(this.originalIndexMap[item.value]),
                    [styles.highlighted]: index === highlightedIndex
                  })}
                  key={`drodown-item-${item.value}`}
                  data-position={index}
                  data-index={this.originalIndexMap[item.value]}
                  onKeyDown={() => {}}
                  onClick={() => this.clickItem(this.originalIndexMap[item.value])}>
                  {withFiltering ? (
                    <HighlightedTextComponent
                      inputText={item.text}
                      highlightPart={filteringParameter}
                    />
                  ) : (
                    item.text
                  )}
                </li>
              ))) || (
              <div className={styles['dropdown-empty-state']}>
                <span className={styles['dropdown-empty-state__text']}>No matches found</span>
              </div>
            )}
          </ol>
        </div>
      </div>
    );
  }
}

Dropdown.propTypes = propTypes;

Dropdown.defaultProps = defaultProps;

export default Dropdown;
